<template>
    <div>
        <CurrentAgency class="mb-3"/>

        <div class="card card-body">
            <StoreTableFilters v-model="filters"
                               :countries="countries"
            />

            <table class="table table-hover table-head-bg">
                <thead>
                <tr>
                    <th>
                        <TableSort field="name" :value="sort" @input="startSort">
                            {{ __('general.store') }}
                        </TableSort>
                    </th>
                    <th>
                        <TableSort field="street1" :value="sort" @input="startSort">
                            {{ __('general.address.address') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="user_exists" :value="sort" @input="startSort">
                            {{ __('general.roles.owner') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="created_at" :value="sort" @input="startSort">
                            {{ __('general.created_at') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="plan_value" :value="sort" @input="startSort">
                            {{ __('general.plan') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="plan" :value="sort" @input="startSort">
                            {{ __('general.active') }}
                        </TableSort>
                    </th>
                    <th class="text-right">{{ __('general.actions') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="store in paginatedStores.data" :key="store.id">
                    <td>
                        <div class="d-flex flex-row">
                            <a :href="storeLink(store)"
                               :class="['btn btn-default text-center', {'opacity-50 disabled' : !store.attributes.active, 'btn-hover-primary' : store.attributes.active}]">
                                <i class="fas fa-eye p-0"/>
                            </a>
                            <div class="pl-3">
                                <div>{{ store.attributes.name }}</div>
                                <div class="text-muted font-size-sm">{{ store.id }}</div>
                            </div>
                        </div>
                    </td>
                    <td>
                        <StoreAddress :store="store" flag/>
                    </td>
                    <td class="text-center">
                        <i v-if="store.owner"
                           class="fas fa-check-circle text-lg"
                           :class="{'cursor-pointer': Permission.MORPH_TO_USER}"
                           data-toggle="tooltip"
                           data-theme="dark"
                           :title="store.owner"
                           @click="morphToUser(store.owner_url_key)"
                        />
                    </td>
                    <td class="text-center">
                        <DateTime :timestamp="store.attributes.created_at"/>
                    </td>
                    <td class="text-center text-muted">
                        <div class="d-flex flex-column align-items-center justify-content-center h-3em"
                             :class="{'text-gold': store.attributes.plan === 'premium'}">
                            <i v-if="store.attributes.plan !== 'free'" class="fas fa-star"></i>
                            <span v-else>-</span>
                            <span class="text-uppercase font-size-xs mt-1">{{ store.attributes.plan }}</span>
                        </div>
                    </td>
                    <td>
                        <Center vertical>
                            <Center class="pl-2">
                                <ToggleModel v-model="store.attributes.active"
                                             confirm-disable="Möchten Sie das Geschäft wirklich deaktivieren?"
                                             :confirm-disable-message="() => storeWarningMessageBody(store)"
                                             @input="active => toggleActive(store, active)"/>
                            </Center>
                        </Center>
                    </td>
                    <TableActions>
                        <a v-if="store.id" :href="$route('store.edit.basic', [store.id])">
                            <TableAction v-if="userPermissions.editStore" :title="__('actions.edit')">
                                <i class="fas fa-edit"/>
                            </TableAction>
                        </a>
                        <a v-if="store.id" href="javascript:void(0);" @click="deleteStore(store)">
                            <TableAction v-if="userPermissions.deleteStore" :title="__('actions.delete')"
                                         hover="btn-hover-light-danger">
                                <i class="fas fa-trash"/>
                            </TableAction>
                        </a>
                    </TableActions>
                </tr>
                </tbody>
            </table>

            <Pagination :pagination="paginatedStores"
                        :loading="loading"
                        @changePage="pageValue => reloadTableData({page: pageValue})"
                        @changePerPage="perPageValue => reloadTableData({per_page: perPageValue})"
                        class="mt-2"
            />
        </div>
    </div>
</template>

<script>
import CurrentAgency from '@components/Agency/CurrentAgency.vue';
import StoreAddress from '@components/Store/StoreAddress.vue';
import TableActions from '@components/Table/TableActions.vue';
import TableAction from '@components/Table/TableAction.vue';
import Pagination from '@components/TableComponents/Pagination.vue';
import StoreTableFilters from '@components/Store/StoreTableFilters.vue';
import TableSort from '@components/Table/TableSort.vue';
import ToggleModel from '@components/ToggleModel.vue';
import Center from '@components/Layout/Center.vue';
import {debounce} from '@composables/debounce';
import {PermissionsMixin} from '@js/permissions';
import DateTime from '@components/DateTime.vue';
import {rewriteUrlNew} from '@composables/rewriteUrlNew'

export default {
    components: {
        DateTime,
        ToggleModel,
        TableSort,
        StoreTableFilters,
        CurrentAgency,
        StoreAddress,
        TableActions,
        TableAction,
        Pagination,
        Center,
    },

    mixins: [PermissionsMixin],

    props: {
        paginatedStores: Object,
        userPermissions: Object,
        countries: Object,
    },

    data() {
        return {
            loading: false,
            filters: {},
            sort: {}
        };
    },

    beforeMount() {
        this.$watch('filters', debounce(() => {
            this.reloadTableData();
        }, 500), {deep: true});
    },

    mounted() {
        this.goToLastPageIfNeeded();
    },

    methods: {
        reloadTableData(requestData = {}) {
            requestData.filters = this.filters;
            this.loading = true;

            this.$inertia.reload({
                data: requestData,
                only: ['paginatedStores'],
                onSuccess: page => {
                    this.loading = false;
                },
                onError: page => {
                    this.loading = false;
                },
            });
        },

        startSort(sorting) {
            this.sort = sorting;
            rewriteUrlNew('sort', sorting);
            this.reloadTableData();
        },

        toggleActive(store, enable) {
            this.loading = true;

            if (enable) {
                this.activateStore(store);
            } else {
                this.deactivateStore(store);
            }
        },

        activateStore(store) {
            if (!this.userPermissions.changeStatus) return;
            axios.patch(this.$route('store.enable', [store.id])).then(response => {
                store.attributes.active = true;
                this.loading = false;
                toastr.success(`${store.attributes.name} wurde aktiviert`, 'Erfolgreich aktiviert');
            }).catch(error => {
                store.attributes.active = false;
                toastr.error(`Konnte ${store.attributes.name} nicht aktivieren`, 'Aktivierung fehlgeschlagen');
            });
        },

        deactivateStore(store) {
            if (!this.userPermissions.changeStatus) return;
            axios.patch(this.$route('store.disable', [store.id])).then(response => {
                store.attributes.active = false;
                this.loading = false;
                toastr.warning(`${store.attributes.name} wurde deaktiviert`, 'Erfolgreich deaktiviert');
            }).catch(error => {
                store.attributes.active = true;
                toastr.error(`Konnte ${store.attributes.name} nicht deaktivieren`, 'Deaktivieren fehlgeschlagen');
            });
        },

        deleteStore(store) {
            Swal.fire({
                title: 'Möchten Sie das Geschäft wirklich löschen?',
                showDenyButton: false,
                showCancelButton: true,
                confirmButtonText: this.__('actions.delete'),
                cancelButtonText: this.__('actions.abort'),
                html: this.storeWarningMessageBody(store),
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.delete(this.$route('store.delete', [store.id])).then(response => {
                        toastr.success(__('store.deleted'));
                        this.$inertia.reload({
                            only: ['paginatedStores'],
                        });
                    });
                }
            });
        },

        storeWarningMessageBody(store) {
            return `<small>${store.attributes.address.street1}, ${store.attributes.address.postcode} ${store.attributes.address.city}, ${store.attributes.address.country.toUpperCase()}</small><br><strong>${store.attributes.name}</strong>`;
        },

        goToLastPageIfNeeded() {
            if (this.paginatedStores.current_page > this.paginatedStores.last_page) {
                this.reloadTableData({page: this.paginatedStores.last_page});
            }
        },

        morphToUser(userUrlKey) {
            if (!this.Permission.MORPH_TO_USER) {
                throw new Error('Access denied.');
            }

            window.location = this.$route('user.morph', {user: userUrlKey});
        },

        storeLink(store) {
            return this.$page.props.frontend_url + store.attributes.link
        },
    },

    watch: {
        'paginatedStores.last_page'(newLastPage) {
            this.goToLastPageIfNeeded();
        },
    },
};
</script>

<style>
.table td {
    vertical-align: middle;
}

.text-gold {
    color: #fcb307;
}

.btn[disabled] {
    cursor: not-allowed;
}
</style>
